import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Zap, Minus, Plus, ShoppingCart, ChevronLeft, ChevronRight, Loader2, CheckCircle, AlertCircle, Truck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PhoneAuthModal from '../components/PhoneAuthModal';
import { productAPI, orderAPI } from '../utils/api';

function ProductDetailPage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '', variant: 'default' });

  const showNotification = (type, message, variant = 'default') => {
    setNotification({ show: true, type, message, variant });
  };

  const closeNotification = (action = null) => {
    const variant = notification.variant;
    setNotification({ show: false, type: 'success', message: '', variant: 'default' });
    if (action === 'cart') navigate('/cart');
    else if (variant === 'back') navigate('/products');
  };

  useEffect(() => {
    loadProduct();
  }, [uuid]);

  const loadProduct = async () => {
    try {
      const data = await productAPI.getDetail(uuid);
      setProduct(data);
      // 최소 수량으로 초기화
      setQuantity(data.min_quantity || 1);
    } catch (err) {
      showNotification('error', '상품을 불러오지 못했습니다.', 'back');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    const hasOptions = product.options && product.options.length > 0;
    if (hasOptions && !selectedOption) {
      showNotification('error', '옵션을 선택해주세요.');
      return;
    }

    try {
      setAdding(true);
      await orderAPI.addToCart(product.uuid, quantity, selectedOption?.name || null);
      showNotification('success', '장바구니에 추가되었습니다.', 'cart');
    } catch (err) {
      showNotification('error', err.message);
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* 이미지 스켈레톤 */}
                <div className="p-6 bg-gray-50 animate-pulse">
                  <div className="aspect-square bg-gray-200 rounded-xl" />
                  <div className="flex gap-2 mt-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-16 lg:w-20 aspect-square bg-gray-200 rounded-lg" />
                    ))}
                  </div>
                </div>
                {/* 상품 정보 스켈레톤 */}
                <div className="p-6 lg:p-8 animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-3/4 mb-3" />
                  <div className="h-10 bg-gray-200 rounded w-1/3 mb-6" />
                  <div className="bg-gray-100 rounded-lg p-4 mb-6">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/4" />
                  </div>
                  <div className="space-y-2 mb-6">
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-5/6" />
                    <div className="h-4 bg-gray-200 rounded w-4/6" />
                  </div>
                  <div className="border-t border-gray-200 pt-6 mt-auto">
                    <div className="flex justify-between items-center mb-4">
                      <div className="h-4 bg-gray-200 rounded w-12" />
                      <div className="h-10 bg-gray-200 rounded w-32" />
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <div className="h-4 bg-gray-200 rounded w-20" />
                      <div className="h-8 bg-gray-200 rounded w-28" />
                    </div>
                    <div className="h-14 bg-gray-200 rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  // 이미지 배열 가져오기 (image_urls 배열 우선, 없으면 image_url 사용)
  const getImages = () => {
    if (product.image_urls && product.image_urls.length > 0) {
      return product.image_urls;
    }
    if (product.image_url) {
      return [product.image_url];
    }
    return ['https://via.placeholder.com/600x600?text=No+Image'];
  };

  const images = getImages();

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* 이미지 섹션 */}
              <div className="p-6 bg-gray-50">
                {/* 메인 이미지 */}
                <div className="relative">
                  <div className="aspect-square w-full">
                    <img
                      src={images[currentImageIndex]}
                      alt={product.name}
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                  {product.has_fast_shipping && (
                    <span className="absolute top-3 left-3 bg-teal-500 text-white text-sm font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-md">
                      <Zap className="w-4 h-4" />
                      빠른 배송
                    </span>
                  )}

                  {/* 캐러셀 네비게이션 */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full transition-all shadow-lg"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full transition-all shadow-lg"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 bg-black/40 px-3 py-1.5 rounded-full">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`h-1.5 rounded-full transition-all ${
                              index === currentImageIndex ? 'bg-white w-4' : 'bg-white/50 w-1.5 hover:bg-white/70'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* 썸네일 이미지 */}
                {images.length > 1 && (
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 w-16 lg:w-20 ${
                          index === currentImageIndex
                            ? 'border-teal-500 shadow-md'
                            : 'border-gray-200 hover:border-teal-300'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 상품 정보 섹션 */}
              <div className="p-6 lg:p-8 flex flex-col">
                {/* 상품 기본 정보 */}
                <div className="flex-1">
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">{product.name}</h1>

                  <div className="text-3xl lg:text-4xl font-bold text-teal-600 mb-6">
                    {product.options && product.options.length > 0 ? (
                      selectedOption ? (
                        selectedOption.price.toLocaleString() + '원'
                      ) : (
                        <span>
                          {Math.min(...product.options.map(o => o.price)).toLocaleString()}원~
                        </span>
                      )
                    ) : (
                      product.price.toLocaleString() + '원'
                    )}
                  </div>

                  {product.description && (
                    <div className="mb-6">
                      <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">
                        {product.description}
                      </p>
                    </div>
                  )}

                  {/* 옵션 선택 */}
                  {product.options && product.options.length > 0 && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">옵션 선택 <span className="text-red-500">*</span></label>
                      <div className="flex flex-wrap gap-2">
                        {product.options.map((opt) => (
                          <button
                            key={opt.name}
                            type="button"
                            onClick={() => setSelectedOption(opt)}
                            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                              selectedOption?.name === opt.name
                                ? 'border-teal-500 bg-teal-50 text-teal-700 shadow-sm'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-teal-300 hover:bg-gray-50'
                            }`}
                          >
                            <span>{opt.name}</span>
                            <span className="ml-2 text-teal-600 font-bold">{opt.price.toLocaleString()}원</span>
                          </button>
                        ))}
                      </div>
                      {!selectedOption && (
                        <p className="text-xs text-gray-400 mt-1.5">옵션을 선택해야 장바구니에 담을 수 있습니다.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* 배송 정보 */}
                {(() => {
                  const hasOptions = product.options && product.options.length > 0;
                  // 옵션 상품이고 아직 선택 안 됨
                  if (hasOptions && !selectedOption) {
                    return (
                      <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-2">
                          <Truck className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-400">옵션을 선택하면 배송 정보가 표시됩니다.</p>
                        </div>
                      </div>
                    );
                  }
                  // 옵션 상품이고 선택됨 → 해당 옵션의 재고로 판단
                  const isFast = hasOptions
                    ? (selectedOption?.fassto_stock_qty ?? 0) > 0
                    : product.has_fast_shipping;
                  const stockQty = hasOptions ? selectedOption?.fassto_stock_qty : product.fassto_stock_qty;

                  return (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      {isFast ? (
                        <div className="flex items-start gap-2">
                          <Zap className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-sm font-medium text-teal-700">빠른배송 (재고: {stockQty}개)</span>
                            <p className="text-sm text-gray-600">
                              배송비 3,000원 (30,000원 이상 주문시 무료) / 한진택배
                            </p>
                            <p className="text-sm text-teal-600 font-medium">
                              오늘 18시 이전 주문시 내일 도착 보장
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">단, 도서산간 지역 제외 · 재고 초과 주문시 일반배송으로 전환</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <Truck className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-sm font-medium text-gray-700">일반배송</span>
                            <p className="text-sm text-gray-600">
                              배송비 3,000원 (30,000원 이상 주문시 무료) / 한진택배
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 구매 영역 - 하단에 붙임 */}
                <div className="border-t border-gray-200 pt-6 mt-auto">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">수량</label>
                    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                      <button
                        onClick={() => {
                          const minQty = product.min_quantity || 1;
                          setQuantity(Math.max(minQty, quantity - 1));
                        }}
                        disabled={quantity <= (product.min_quantity || 1)}
                        className="p-3 text-gray-600 hover:bg-gray-100 transition-colors disabled:text-gray-300 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min={product.min_quantity || 1}
                        max={product.max_quantity || undefined}
                        value={quantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          const minQty = product.min_quantity || 1;
                          const maxQty = product.max_quantity;
                          if (!isNaN(value) && value >= minQty) {
                            if (maxQty && value > maxQty) {
                              setQuantity(maxQty);
                            } else {
                              setQuantity(value);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = parseInt(e.target.value);
                          const minQty = product.min_quantity || 1;
                          const maxQty = product.max_quantity;
                          if (isNaN(value) || value < minQty) {
                            setQuantity(minQty);
                          } else if (maxQty && value > maxQty) {
                            setQuantity(maxQty);
                          }
                        }}
                        className="w-16 text-center font-medium bg-white border-x border-gray-300 py-2 focus:outline-none"
                      />
                      <button
                        onClick={() => {
                          const maxQty = product.max_quantity;
                          if (!maxQty || quantity < maxQty) {
                            setQuantity(quantity + 1);
                          }
                        }}
                        disabled={product.max_quantity && quantity >= product.max_quantity}
                        className="p-3 text-gray-600 hover:bg-gray-100 transition-colors disabled:text-gray-300 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {/* 수량 제한 안내 */}
                  {(product.min_quantity > 1 || product.max_quantity) && (
                    <p className="text-xs text-gray-500 text-right mb-4">
                      {product.min_quantity > 1 && `최소 ${product.min_quantity}개`}
                      {product.min_quantity > 1 && product.max_quantity && ' / '}
                      {product.max_quantity && `최대 ${product.max_quantity}개`}
                    </p>
                  )}
                  {!(product.min_quantity > 1 || product.max_quantity) && <div className="mb-4" />}

                  {/* 총 가격 */}
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-600">총 상품금액</span>
                    <span className="text-2xl font-bold text-teal-600">
                      {product.options && product.options.length > 0 ? (
                        selectedOption
                          ? (selectedOption.price * quantity).toLocaleString() + '원'
                          : '-'
                      ) : (
                        (product.price * quantity).toLocaleString() + '원'
                      )}
                    </span>
                  </div>

                  <button
                    onClick={addToCart}
                    disabled={adding}
                    className="w-full bg-teal-500 text-white py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {adding ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        추가 중...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5" />
                        장바구니 담기
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 상세 설명 섹션 */}
          {product.detail_description && (
            <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">상세 설명</h2>
              <div className="prose max-w-none text-gray-700">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  components={{
                    img: ({node, ...props}) => (
                      <img {...props} className="max-w-full h-auto rounded-lg my-4" alt={props.alt || ''} />
                    ),
                    h1: ({node, ...props}) => (
                      <h1 {...props} className="text-2xl font-bold mt-6 mb-3 text-gray-900" />
                    ),
                    h2: ({node, ...props}) => (
                      <h2 {...props} className="text-xl font-bold mt-5 mb-2 text-gray-900" />
                    ),
                    h3: ({node, ...props}) => (
                      <h3 {...props} className="text-lg font-bold mt-4 mb-2 text-gray-800" />
                    ),
                    ul: ({node, ...props}) => (
                      <ul {...props} className="list-disc list-inside my-3 space-y-1" />
                    ),
                    ol: ({node, ...props}) => (
                      <ol {...props} className="list-decimal list-inside my-3 space-y-1" />
                    ),
                    p: ({node, ...props}) => (
                      <p {...props} className="my-2 leading-relaxed" />
                    ),
                    a: ({node, ...props}) => (
                      <a {...props} className="text-teal-600 hover:text-teal-700 hover:underline" target="_blank" rel="noopener noreferrer" />
                    ),
                  }}
                >
                  {product.detail_description}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <PhoneAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          addToCart();
        }}
      />

      {/* 알림 모달 */}
      {notification.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex flex-col items-center text-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${
                notification.type === 'success' ? 'bg-teal-100' : 'bg-red-100'
              }`}>
                {notification.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-teal-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
              </div>
              <p className="text-gray-800 font-medium mb-5">{notification.message}</p>
              {notification.variant === 'cart' ? (
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => closeNotification()}
                    className="flex-1 py-3 rounded-lg font-semibold transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
                  >
                    쇼핑 계속하기
                  </button>
                  <button
                    onClick={() => closeNotification('cart')}
                    className="flex-1 py-3 rounded-lg font-semibold transition-colors bg-teal-500 hover:bg-teal-600 text-white"
                  >
                    장바구니
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => closeNotification()}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    notification.type === 'success'
                      ? 'bg-teal-500 hover:bg-teal-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  확인
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetailPage;
